import React, {PureComponent} from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Row, Col } from 'react-bootstrap';
import request from 'axios';
import LoadingProgressBar from './LoadingProgressBar';
import ConfirmDialog from './ConfirmDialog';
import Button from './Button';
import Table from './Table';
import { getClientAuthHeader } from '../../../client/token';
import { userDownloadFile } from '../../actions/userActions';
import { getFileExtension, getWordPlaceholders, isDemoAccount } from '../../helpers/utils';

import FileInput from 'react-fine-uploader/file-input';
import { isDriveIntegrated } from './DriveIntegration';
import { FileComponents } from './../../helpers/fileUtils';
import { caseSetServerError } from './../../actions/caseActions';

let FineUploaderS3;
let FineUploader;
let Dropzone;


class FileUpload extends PureComponent {
    constructor(props) {
        super(props);
        this.onUploadError = this.onUploadError.bind(this);
        this.onValidate = this.onValidate.bind(this);
        this.onSubmit = this.onSubmit.bind(this);
        this.onUploadFinish = this.onUploadFinish.bind(this);
        this.onUploadProgress = this.onUploadProgress.bind(this);
        this.onUploadSubmitted = this.onUploadSubmitted.bind(this);
        this.downloadRow = this.downloadRow.bind(this);
        this.state = {
            uploadedPercent: undefined,
            loading: false,
            deleteFileDialogOpen: false,
            deleteDialogConfirm: undefined,
            deleteFileName: undefined
        };
    }

    onUploadError(id, name, errorReason) {
        this.setState({percentUploaded: undefined, loading: false});
        if ((errorReason || '').toLowerCase().indexOf('expired') != -1) {
            errorReason = "Please check your computer date / time settings and try again.";
        }
        if (this.props.onError) {
            this.props.onError(name, errorReason);
        }
    }

    onValidate(data) {
        let extension = getFileExtension(data.name);
        let sizeLimit = data.size <= this.props.sizeLimit;
        let validExtension = this.props.extensions.findIndex((ext) => {return ext === extension || ext === '*'}) > -1;

        let result = sizeLimit && validExtension;
        if (!result && this.props.onError) {
            let error;
            if (!validExtension) {
                error = `Only these extensions supported: ${this.props.extensions.reduce((a, b) => `${a}, ${b}`)}`;
            }

            if (!sizeLimit) {
                error = 'File is too big.';
            }

            this.props.onError(name, error);
        }

        // Demo accounts pretend the upload succeeded: show the normal loading
        // flow and record it on the case's event stream, but never hand the
        // file to the uploader (the server refuses demo uploads as a backstop).
        if (result && isDemoAccount(this.props.loggedIn)) {
            this.simulateDemoUpload(data.name);
            return false;
        }

        return result;
    }

    simulateDemoUpload(name) {
        this.setState({ loading: true, percentUploaded: 100 });

        const components = new FileComponents(`${this.props.folder}/${name}`);
        if (components.isCaseFile()) {
            const event = {
                EventType: 'Note',
                CaseId: parseInt(components.getCaseId(), 10),
                EventDescription: `Uploaded document "${name}" (live demo — the file is not stored)`,
                EventDate: new Date().toISOString(),
                version: 1,
            };

            request.put('/api/v1/event', event, getClientAuthHeader()).catch(() => {});
        }

        setTimeout(() => {
            this.setState({ loading: false, percentUploaded: undefined });
        }, 700);
    }

    onSubmit(id, name) {
        if (this.uploader?.methods?.getFile && name?.toLowerCase().endsWith('.docx')) {
            return new Promise((resolve, reject) => {
                const data = this.uploader.methods.getFile(id);
                const reader = new FileReader();
                reader.onload = (event) => {
                    try {
                        const content = reader.result;
                        const placeholders = getWordPlaceholders(content);
                        const placeholdersStr = JSON.stringify(placeholders);
                        // 2KB is max limit for custom metadata.
                        if (placeholdersStr.length < 1800) {
                            this.uploader.methods.setParams({ 'x-amz-meta-placeholders': placeholdersStr }, id);
                        }
                    } catch (error) {
                        console.error(error);
                        this.props.dispatch(caseSetServerError('There have been errors in your document, please check placeholders.'));
                        reject(error);
                    }
                    resolve();
                }
                
                reader.readAsArrayBuffer(data);
            });
        }
    }

    onUploadFinish(id, name, responseJSON) {
        if (responseJSON.success) {
            this.setState({
                serverError: undefined,
                loading: false,
                percentUploaded: undefined
            });

            if (this.props.onFinish) {
                const size = this.uploader.methods.getSize(id);
                this.props.onFinish(name, size);
            }

        } else {
            this.setState({
                serverError: this.props.serverError,
                loading: false,
                percentUploaded: undefined
            });
        }
    }

    onUploadProgress(id, name, uploadedBytes, totalBytes) {
        let uploadedPercent = 0;
        if (totalBytes > 0) {
            uploadedPercent = (uploadedBytes / totalBytes) * 100;
        }

        this.setState({
            serverError: undefined,
            loading: true,
            percentUploaded: uploadedPercent
        });
    }

    onUploadSubmitted(id, name) {
        this.setState({
            loading: true,
        });

        if (this.props.onSubmit) {
            this.props.onSubmit(name)
        }
    }

    downloadRow(rowIndex, row) {
        let headers = this.props.headers;
        if (!headers || headers.length < 1) {
            return;
        }

        let rowData = row[headers[0]['table']];
        let downloadPath = rowData['$DownloadPath$'];
        if (this.props.downloadRow) {
            this.props.downloadRow(rowData, userDownloadFile(downloadPath));
            return;
        }

        this.props.dispatch(userDownloadFile(downloadPath));
    }

    componentDidMount() {
        this.componentWillReceiveProps(this.props);
    }

    async componentWillReceiveProps(nextProps) {
        if (!__CLIENT__ || !nextProps.loggedIn || !nextProps.folder || this.uploader) {
            return;
        }

        if (!Dropzone) {
            Dropzone = (await import('react-fine-uploader/dropzone')).default;
            FineUploader = (await import('fine-uploader-wrappers/traditional')).default;
            FineUploaderS3 = (await import('fine-uploader-wrappers/s3')).default;
        }

        let authHeaders = getClientAuthHeader();
        authHeaders = authHeaders.headers;
        const options = {
            debug: __DEVELOPMENT__,
            callbacks: {
                onComplete: this.onUploadFinish,
                onError: this.onUploadError,
                onProgress: this.onUploadProgress,
                onSubmitted: this.onUploadSubmitted,
                onValidate: this.onValidate,
                onSubmit: this.onSubmit,
            },
            validation: {
                stopOnFirstInvalidFile: true
            }
        };

        // TODO: Do not upload templates to gdrive yet.
        const components = new FileComponents(nextProps.folder);
        const useExternalDriveUploader = isDriveIntegrated(nextProps.loggedIn) && !components.isTemplateFile();
        if (useExternalDriveUploader) {
            options['request'] = {
                endpoint: `/api/v1/uploads/external_storage/${encodeURIComponent(nextProps.folder)}`,
                customHeaders: authHeaders
            };

            // No chunking supported. Slower for big files, but those are rare if any.
            options['chunking'] = {
                enabled: false
            };

            this.uploader = new FineUploader({options});

        } else {
            options['request'] = {
                endpoint: `${window.location.protocol}//${process.env.AWS_S3_BUCKET}.s3.amazonaws.com`,
                accessKey: process.env.AWS_CLIENT_PUBLIC_ACCESS_KEY_ID
            };

            options['signature'] = {
                customHeaders: authHeaders,
                endpoint: '/api/v1/uploads/signature'
            };

            options['objectProperties'] = {
                key: (id) => {
                    const key = `${this.props.folder}/${this.uploader.methods.getName(id)}`;
                    return key;
                }
            };

            this.uploader = new FineUploaderS3({options});
        }

        this.forceUpdate();
    }


    render() {
        const {
            title,
            headers,
            rows,
            loading,
            renderFiles = true,
            renderUpload = true,
            manualUpload = undefined,
            customFieldsMap
        } = this.props;
 
        let deleteRowConfirm;
        if (this.props.onDelete) {
            deleteRowConfirm = (rowIndex, row) => {
                this.setState({
                    deleteFileDialogOpen: true,
                    deleteFileName: row && row.data && row.data['Name'],
                    deleteDialogConfirm: () => {
                        this.props.onDelete(rowIndex, row);
                        this.setState({deleteFileDialogOpen: false});
                    }
                });
            };
        }

        return (
            <div>
                {renderFiles && (
                <div>
                <Row>
                <Col xs={12}>
                    <LoadingProgressBar loading={this.state.loading || loading}
                                        percent={this.state.percentUploaded ? this.state.percentUploaded : 100} />
                </Col>
                </Row>
                {!this.state.loading && !loading && (
                <Row>
                    <Col xs={12} style={{minHeight: 330}}>
                    <Table
                        headers={headers}
                        rows={rows}
                        customFieldsMap={customFieldsMap}
                        deleteRow={deleteRowConfirm}
                        deleteRows={this.props.onDeleteMany}
                        deleteBsStyle="link"
                        downloadRow={this.downloadRow}
                        downloadBsStyle="link"
                        filterable={rows.length > 5}
                        defaultPageSize={rows.length > 5 ? 5 : 0}
                        loading={this.state.loading || loading}
                        getTdProps={this.props.getTdProps}
                        customActionColumn={this.props.customActionColumn}
                        getRef={tableRef => {
                            if (this.props.getRef) {
                                this.props.getRef(tableRef);
                            }
                        }}
                    />
                </Col>
                </Row>)}
                <ConfirmDialog
                    title='Delete?'
                    text={`Do you really want to delete '${this.state.deleteFileName}'?`}
                    warning
                    cancel={() => {this.setState({deleteFileDialogOpen: false})}}
                    confirm={this.state.deleteDialogConfirm}
                    open={this.state.deleteFileDialogOpen}>
                </ConfirmDialog>
                </div>)}
                {this.uploader && renderUpload && (
                    <div>
                    <Row  style={{marginTop: renderFiles ? 15 : 0}}>
                        <Col xs={12}>
                            <Dropzone style={{border: '2px dashed #DDD', height: "150px", width: "100%", textAlign: 'center'}}
                                      uploader={this.uploader}>
                                    <div style={{marginTop: 40}}><img src='/img/upload.png'/><h4>Drag files to upload</h4></div>
                            </Dropzone>
                        </Col>
                    </Row>
                    <Row>
                        <Col xs={12} style={{marginTop: 15, textAlign: 'center'}}>
                            <FileInput multiple uploader={this.uploader}>
                                <Button label="Choose File"
                                        bsStyle="primary" />
                            </FileInput>
                        </Col>
                    </Row>
                    </div>
                    )}
                    {this.uploader && manualUpload && (
                        <Button bsStyle="primary"
                                label={title}
                                loading={this.state.loading || loading}
                                disabled={this.state.loading || loading}
                                icon="cloud-upload"
                                onClick={(evt) => {
                                    evt.preventDefault();
                                    manualUpload(this.uploader);
                                }}
                                block
                        />
                    )}
            </div>);
    }
}

FileUpload.propTypes = {
    extensions: PropTypes.array.isRequired,
    sizeLimit: PropTypes.number,
    title: PropTypes.string.isRequired,
    folder: PropTypes.string.isRequired,
    onError: PropTypes.func,
    onFinish: PropTypes.func,
    onSubmit: PropTypes.func,
    onDelete: PropTypes.func,
    loading: PropTypes.bool,
    headers: PropTypes.array,
    height: PropTypes.string,
    rows: PropTypes.array, // value must have DownloadPath property
    renderUpload: PropTypes.bool,
    renderFiles: PropTypes.bool,
    manualUpload: PropTypes.func,
    customFieldsMap: PropTypes.object,
    downloadRow: PropTypes.func,
    customActionColumn: PropTypes.object,
};


const mapStateToProps = (state, ownProps) => {
    return {
        loggedIn: state.user.loggedIn
    };
};


export default connect(mapStateToProps)(FileUpload);
